import { useCallback, useEffect, useRef, useState } from "react";
import Konva from "konva";
import { useApp } from "../../../../context/AppContext";
import { ChartDocument, SavedChartMeta } from "../types/chart";
import { exportChartAsBlob } from "../utils/chartExport";
import {
  buildChartMarkupPngBlob,
  buildChartMarkupUploadMetadata,
  parseChartDocumentFromPngBytes,
  parseChartDocumentFromVersionMetadata,
} from "../utils/chartMarkupDocument";
import { CHART_DISPLAY_NAME_MAX_CHARS } from "../constants/chartUploadLimits";
import {
  extractChartTitleFromDocumentDetailResponse,
  getChartListTitle,
  normalizeSalonDocumentFromListApi,
} from "../utils/chartListTitle";

/** Signed URLs expire after 15 min; refresh before that */
const SIGNED_URL_STALE_MS = 12 * 60 * 1000;

function generateDocumentKey(
  clientId: string,
  suffix: "data" | "render",
): string {
  const ts = Date.now();
  // Slug format: [a-z0-9][a-z0-9_-]{0,127}
  return `chart-${clientId}-${ts}-${suffix}`
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "-");
}

function extractTimestamp(documentKey: string): string {
  /** Keys are `chart-{clientId}-{unixMs}-data|render` — clientId may contain hyphens (UUID). */
  const m = documentKey.match(/-(\d{10,16})-(?:data|render)$/);
  if (m) {
    const ts = Number(m[1]);
    if (!Number.isNaN(ts) && ts > 0) {
      return new Date(ts).toISOString();
    }
  }
  return new Date().toISOString();
}

function fallbackChartLabel(documentKey: string): string {
  return `Chart ${new Date(extractTimestamp(documentKey)).toLocaleDateString()}`;
}

async function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    out.push(...(await Promise.all(batch.map(fn))));
  }
  return out;
}

export function useChartPersistence(salonId: string, clientId: string) {
  const {
    listSalonDocuments,
    uploadSalonDocument,
    deleteSalonDocument,
    getSalonDocumentDetail,
  } = useApp();
  const [charts, setCharts] = useState<SavedChartMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const lastRefreshRef = useRef<number>(0);

  const refreshCharts = useCallback(async () => {
    if (!salonId || !clientId) return;
    setLoading(true);
    try {
      const response = await listSalonDocuments(salonId, clientId);
      const docs = response?.documents || [];

      // Filter for chart data documents (not render docs)
      const chartDocs = docs.filter((d) => {
        const r = d as unknown as Record<string, unknown>;
        const key = r.documentKey ?? r.document_key;
        return typeof key === "string" && key.endsWith("-data");
      });

      const listMetas: SavedChartMeta[] = chartDocs.map((raw) => {
        const doc = normalizeSalonDocumentFromListApi(raw);
        const renderKey = doc.documentKey.replace(/-data$/, "-render");
        const renderDoc = docs.find((d) => {
          const r = d as unknown as Record<string, unknown>;
          const key = r.documentKey ?? r.document_key;
          return key === renderKey;
        });
        const renderNorm = renderDoc
          ? normalizeSalonDocumentFromListApi(renderDoc)
          : null;
        const createdAt =
          doc.currentCreatedDt || extractTimestamp(doc.documentKey);

        return {
          documentKey: doc.documentKey,
          renderDocumentKey: renderKey,
          renderUrl: renderNorm?.currentUrl || "",
          createdAt,
          title: getChartListTitle(doc, fallbackChartLabel(doc.documentKey)),
        };
      });

      /** List rows often omit displayName / chart metadata — fill from document detail. */
      const metas = await mapInBatches(listMetas, 5, async (m) => {
        const fb = fallbackChartLabel(m.documentKey);
        if (m.title !== fb) return m;
        try {
          const detail = await getSalonDocumentDetail(
            salonId,
            m.documentKey,
            clientId,
          );
          const t = extractChartTitleFromDocumentDetailResponse(detail);
          if (t) return { ...m, title: t };
        } catch (err) {
          console.warn(
            "[useChartPersistence] Could not enrich chart title from detail",
            m.documentKey,
            err,
          );
        }
        return m;
      });

      // Sort newest first
      metas.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setCharts(metas);
      lastRefreshRef.current = Date.now();
    } catch (error) {
      console.error("Failed to load charts:", error);
      setCharts([]);
    } finally {
      setLoading(false);
    }
  }, [salonId, clientId, listSalonDocuments, getSalonDocumentDetail]);

  // Refresh signed URLs when the tab regains focus if they're stale
  useEffect(() => {
    if (!salonId || !clientId) return;

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const elapsed = Date.now() - lastRefreshRef.current;
      if (elapsed >= SIGNED_URL_STALE_MS) {
        refreshCharts();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [salonId, clientId, refreshCharts]);

  // Fallback interval for when the tab stays visible continuously.
  // Skip when there are no charts to refresh — refreshing only matters for
  // expiring signed URLs, and an empty list means there are none.
  useEffect(() => {
    if (!salonId || !clientId) return;
    if (charts.length === 0) return;

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshCharts();
      }
    }, SIGNED_URL_STALE_MS);

    return () => clearInterval(interval);
  }, [salonId, clientId, charts.length, refreshCharts]);

  const saveChart = useCallback(
    async (
      document: ChartDocument,
      stage: Konva.Stage,
      existingDocumentKey?: string,
    ) => {
      if (!salonId || !clientId) {
        throw new Error(
          "Salon and client information required to save charts.",
        );
      }

      // Use existing key (update) or generate new one
      const dataKey =
        existingDocumentKey || generateDocumentKey(clientId, "data");
      const renderKey = dataKey.replace(/-data$/, "-render");

      // 1. Serialize and upload the annotation JSON
      const docWithMeta: ChartDocument = {
        ...document,
        metadata: {
          ...document.metadata,
          clientId,
          updatedAt: new Date().toISOString(),
          createdAt: document.metadata?.createdAt || new Date().toISOString(),
        },
      };
      // Embed the chart JSON inside the PNG body (as a tEXt chunk) instead of
      // shoving it into upload metadata. Backend metadata fields have varied,
      // sometimes-tiny size limits; the document body has none we care about.
      const chartPng = buildChartMarkupPngBlob(docWithMeta);
      const trimmedTitle = docWithMeta.metadata?.title
        ?.trim()
        .slice(0, CHART_DISPLAY_NAME_MAX_CHARS);
      /** Always send a display name so list APIs can show a label even if metadata is omitted. */
      const displayTitle =
        trimmedTitle ||
        fallbackChartLabel(dataKey).slice(0, CHART_DISPLAY_NAME_MAX_CHARS);
      await uploadSalonDocument(
        salonId,
        dataKey,
        chartPng,
        "image/png",
        buildChartMarkupUploadMetadata(docWithMeta, clientId),
        clientId,
        displayTitle,
      );

      // 2. Export and upload the flattened PNG
      try {
        const pngBlob = await exportChartAsBlob(stage);
        await uploadSalonDocument(
          salonId,
          renderKey,
          pngBlob,
          "image/png",
          { type: "chart_render", clientId },
          clientId,
        );
      } catch (err) {
        // PNG export is nice-to-have; don't fail the save
        console.error("Failed to export chart render:", err);
      }

      // Refresh the chart list
      await refreshCharts();
    },
    [salonId, clientId, uploadSalonDocument, refreshCharts],
  );

  const loadChart = useCallback(
    async (documentKey: string): Promise<ChartDocument | null> => {
      if (!salonId) return null;
      try {
        const detail = await getSalonDocumentDetail(
          salonId,
          documentKey,
          clientId,
        );
        const versions = detail?.versions || [];
        if (versions.length === 0) return null;

        const current = versions.reduce((best, v) =>
          v.versionNumber > best.versionNumber ? v : best,
        );
        // Legacy saves embedded the JSON in version metadata. New saves embed
        // it in the PNG body via a tEXt chunk — try metadata first (cheap),
        // then fall back to fetching and parsing the body.
        const fromMeta = parseChartDocumentFromVersionMetadata(
          current.metadata ?? undefined,
        );
        if (fromMeta) return fromMeta;
        if (!current.url) return null;
        const res = await fetch(current.url);
        if (!res.ok) return null;
        const buffer = await res.arrayBuffer();
        return parseChartDocumentFromPngBytes(new Uint8Array(buffer));
      } catch (error) {
        console.error("Failed to load chart:", error);
        throw error;
      }
    },
    [salonId, clientId, getSalonDocumentDetail],
  );

  const deleteChart = useCallback(
    async (documentKey: string) => {
      if (!salonId) return;
      const renderKey = documentKey.replace(/-data$/, "-render");

      // Delete both data and render documents (pass clientId for PHI scoping)
      await Promise.all([
        deleteSalonDocument(salonId, documentKey, clientId || undefined).catch(
          (err) => console.error("Failed to delete chart data:", err),
        ),
        deleteSalonDocument(salonId, renderKey, clientId || undefined).catch(
          (err) => console.error("Failed to delete chart render:", err),
        ),
      ]);

      await refreshCharts();
    },
    [salonId, clientId, deleteSalonDocument, refreshCharts],
  );

  return {
    charts,
    loading,
    saveChart,
    loadChart,
    deleteChart,
    refreshCharts,
  };
}
